import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";

import {
    useAdminPackages,
    useCreatePackage,
    useUpdatePackage,
    useDeletePackage,
    useCreatePackageItinerary,
    useUpdatePackageItinerary,
    useDeletePackageItinerary,
} from "@/services/adminPackageService";
import type { Package, PackageItinerary } from "@/services/adminPackageService";
import { useAdminPeriods } from "@/services/adminPeriodService";
import ConferencePackagesTab from "./ConferencePackagesTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Edit2, Plus, Trash2, PackageSearch, Loader2, Image as ImageIcon, Search, Clock, X } from "lucide-react";


const formSchema = z.object({
    period_id: z.string().min(1, "Periode wajib dipilih"),
    parent_id: z.string().optional().nullable(),
    code: z.string().min(1, "Kode wajib diisi"),
    name: z.string().min(1, "Nama paket wajib diisi"),
    description: z.string().optional(),
    price: z.coerce.number().min(0, "Harga tidak boleh negatif"),
    is_header: z.boolean().default(false),
    is_quota: z.boolean().default(false),
    quota: z.coerce.number().min(0).optional(),
});
type FormValues = z.infer<typeof formSchema>;

export default function PackagesPage() {
    const { data: packages, isLoading: isLoadingPackages } = useAdminPackages();
    const { data: periods, isLoading: isLoadingPeriods } = useAdminPeriods();
    
    const createMutation = useCreatePackage();
    const updateMutation = useUpdatePackage();
    const deleteMutation = useDeletePackage();

    const [activeTab, setActiveTab] = useState("konvensi");
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingPackage, setEditingPackage] = useState<Package | null>(null);
    const [itineraryPackage, setItineraryPackage] = useState<Package | null>(null);

    // Alert dialog state
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [alertConfig, setAlertConfig] = useState<{
        title: string;
        description: string;
        actionText: string;
        action: () => void;
        isDestructive?: boolean;
    } | null>(null);

    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Client-side pagination and search state
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [search, setSearch] = useState("");
    const [selectedPeriodId, setSelectedPeriodId] = useState<string>("all");

    // Client-side filtering and pagination logic
    const filteredPackages = (packages || []).filter(pkg => 
        (selectedPeriodId === "all" || pkg.period_id.toString() === selectedPeriodId) &&
        (pkg.name.toLowerCase().includes(search.toLowerCase()) || 
        pkg.code.toLowerCase().includes(search.toLowerCase()) ||
        (pkg.description && pkg.description.toLowerCase().includes(search.toLowerCase())))
    );
    const totalItems = filteredPackages.length;
    const totalPages = Math.ceil(totalItems / perPage) || 1;
    const paginatedPackages = filteredPackages.slice((page - 1) * perPage, page * perPage);
    const fromItem = totalItems === 0 ? 0 : (page - 1) * perPage + 1;
    const toItem = Math.min(page * perPage, totalItems);

    const headerPackagesCount = (packages || []).filter(p => p.is_header).length;
    const childPackagesCount = (packages || []).filter(p => !p.is_header).length;

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            period_id: "",
            parent_id: "",
            code: "",
            name: "",
            description: "",
            price: 0,
            is_header: false,
            is_quota: false,
            quota: 0,
        },
    });

    const isQuota = form.watch("is_quota");
    const activePeriodFilter = form.watch("period_id");

    const handleOpenDialog = (pkg?: Package) => {
        setPhotoFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";

        if (pkg) {
            setEditingPackage(pkg);
            form.reset({
                period_id: pkg.period_id.toString(),
                parent_id: pkg.parent_id ? pkg.parent_id.toString() : "0",
                code: pkg.code,
                name: pkg.name,
                description: pkg.description || "",
                price: Number(pkg.price),
                is_header: Boolean(pkg.is_header),
                is_quota: Boolean(pkg.is_quota),
                quota: pkg.quota || 0,
            });
        } else {
            setEditingPackage(null);
            form.reset({
                period_id: periods?.[0]?.period_id.toString() || "",
                parent_id: "0",
                code: "",
                name: "",
                description: "",
                price: 0,
                is_header: false,
                is_quota: false,
                quota: 0,
            });
        }
        setIsDialogOpen(true);
    };

    const onSubmit = (values: FormValues) => {
        const formData = new FormData();
        formData.append("period_id", values.period_id);
        
        if (values.parent_id && values.parent_id !== "0") {
            formData.append("parent_id", values.parent_id);
        } else {
            formData.append("parent_id", "");
        }
        
        formData.append("code", values.code);
        formData.append("name", values.name);
        formData.append("description", values.description || "");
        formData.append("price", values.price.toString());
        formData.append("is_header", values.is_header ? "1" : "0");
        formData.append("is_quota", values.is_quota ? "1" : "0");
        
        if (values.is_quota) {
            formData.append("quota", values.quota?.toString() || "0");
        }
        
        if (photoFile) {
            formData.append("photo", photoFile);
        }

        if (editingPackage) {
            updateMutation.mutate(
                { id: editingPackage.package_id, payload: formData },
                {
                    onSuccess: () => {
                        toast.success("Paket berhasil diperbarui");
                        setIsDialogOpen(false);
                    },
                    onError: (err: any) => {
                        toast.error(err.response?.data?.message || "Gagal memperbarui paket");
                    },
                }
            );
        } else {
            createMutation.mutate(formData, {
                onSuccess: () => {
                    toast.success("Paket berhasil dibuat");
                    setIsDialogOpen(false);
                },
                onError: (err: any) => {
                    toast.error(err.response?.data?.message || "Gagal membuat paket");
                },
            });
        }
    };

    const handleDelete = (id: number) => {
        setAlertConfig({
            title: "Hapus Paket",
            description: "Apakah Anda yakin ingin menghapus paket ini? Tindakan ini tidak dapat dibatalkan.",
            actionText: "Hapus",
            isDestructive: true,
            action: () => {
                deleteMutation.mutate(id, {
                    onSuccess: () => toast.success("Paket berhasil dihapus"),
                    onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal menghapus paket"),
                });
            }
        });
        setIsAlertOpen(true);
    };

    const isMutating = createMutation.isPending || updateMutation.isPending;

    // Filter potential parent packages based on selected period
    const parentOptions = packages?.filter(
        p => Boolean(p.is_header) && p.period_id.toString() === activePeriodFilter && p.package_id !== editingPackage?.package_id
    ) || [];

    const formatCurrency = (amount: string | number) => {
        return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(Number(amount));
    };

    // Ambil versi terbaru dari list (bukan snapshot lama) supaya dialog rincian jadwal
    // langsung ter-update setelah tambah/edit/hapus item tanpa perlu ditutup-buka lagi.
    const currentItineraryPackage = itineraryPackage
        ? packages?.find((p) => p.package_id === itineraryPackage.package_id) || itineraryPackage
        : null;

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            {/* --- HEADER --- */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 bg-white dark:bg-slate-900 p-6 md:p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                            <PackageSearch className="w-6 h-6" />
                        </div>
                        <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Kelola Paket</h1>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                        Kelola daftar paket pendaftaran untuk Konaspi.
                    </p>
                </div>
                {activeTab === "konvensi" && (
                    <div className="flex items-center gap-3 shrink-0 flex-wrap">
                        <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-11 px-6 font-bold shadow-md transition-all">
                            <Plus className="w-4 h-4 mr-2" /> Tambah Paket
                        </Button>
                    </div>
                )}
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="mb-2 bg-slate-100 dark:bg-slate-800/50 p-1.5 rounded-xl w-full sm:w-auto flex overflow-x-auto border border-slate-200 dark:border-slate-700 justify-start h-auto">
                    <TabsTrigger value="konvensi" className="rounded-lg font-bold data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:shadow-sm px-6 py-2.5">Paket Konvensi</TabsTrigger>
                    <TabsTrigger value="conference" className="rounded-lg font-bold data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:shadow-sm px-6 py-2.5">Paket Conference</TabsTrigger>
                </TabsList>

                <TabsContent value="konvensi" className="mt-0 outline-none focus:outline-none space-y-8">

            {/* --- SUMMARY CARDS --- */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px] relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <PackageSearch className="w-12 h-12 text-indigo-500" />
                    </div>
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><PackageSearch className="w-4 h-4 text-indigo-500" /> Total Paket</p>
                    <p className="text-3xl font-black text-slate-900 dark:text-white">{(packages || []).length}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px] relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <PackageSearch className="w-12 h-12 text-amber-500" />
                    </div>
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-amber-500"></div> Paket Induk</p>
                    <p className="text-3xl font-black text-slate-900 dark:text-white">{headerPackagesCount}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px] relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <PackageSearch className="w-12 h-12 text-emerald-500" />
                    </div>
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-emerald-500"></div> Sub Paket (Child)</p>
                    <p className="text-3xl font-black text-slate-900 dark:text-white">{childPackagesCount}</p>
                </div>
            </div>

            {/* --- TOOLBAR (FILTER & SEARCH) --- */}
            <div className="flex flex-col gap-4 w-full">
                <div className="flex flex-wrap items-center gap-3 w-full">
                    <div className="bg-white dark:bg-slate-900 px-4 h-11 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 flex items-center min-w-[200px]">
                        <Select value={selectedPeriodId} onValueChange={(val) => { setSelectedPeriodId(val); setPage(1); }}>
                            <SelectTrigger className="w-full bg-transparent border-none shadow-none h-full p-0 focus:ring-0 text-sm font-bold truncate">
                                <SelectValue placeholder="Pilih Acara (Periode)" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua Acara (Periode)</SelectItem>
                                {periods?.map(p => (
                                    <SelectItem key={p.period_id} value={p.period_id.toString()}>
                                        {p.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="relative flex-1 min-w-[250px]">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Cari kode, nama paket..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            className="w-full h-11 pl-11 pr-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none shadow-sm transition-all font-medium"
                        />
                    </div>
                </div>
            </div>

            {/* --- DATA TABLE --- */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
                    <div className="overflow-x-auto">
                        {isLoadingPackages || isLoadingPeriods ? (
                            <div className="flex justify-center items-center py-20">
                                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                            </div>
                        ) : (
                            <table className="w-full text-left text-sm whitespace-nowrap">
                                <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                                    <tr>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 w-[50px] text-center">No</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Kode</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Nama Paket</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Harga</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-center">Kuota</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-center">Header</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Periode</th>
                                        <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-right sticky right-0 z-10 bg-slate-50 dark:bg-slate-800/90 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)]">Aksi</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {paginatedPackages && paginatedPackages.length > 0 ? paginatedPackages.map((pkg, index) => (
                                        <tr key={pkg.package_id} className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors bg-white dark:bg-slate-900">
                                            <td className="px-6 py-4 text-center font-medium">{(page - 1) * perPage + index + 1}</td>
                                            <td className="px-6 py-4 font-semibold text-slate-900 dark:text-slate-100">{pkg.code}</td>
                                            <td className="px-6 py-4">
                                                <div className="font-medium text-indigo-600 dark:text-indigo-400">{pkg.name}</div>
                                                {pkg.parent && (
                                                    <div className="text-[11px] text-slate-500 mt-1">
                                                        Induk: {pkg.parent.name}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 font-medium text-slate-700 dark:text-slate-300">{formatCurrency(pkg.price)}</td>
                                            <td className="px-6 py-4 text-center font-medium">
                                                {pkg.is_quota ? (
                                                    <span className="text-indigo-600 dark:text-indigo-400">{pkg.quota}</span>
                                                ) : (
                                                    <span className="text-slate-400">-</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                {pkg.is_header ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">Ya</span>
                                                ) : "-"}
                                            </td>
                                            <td className="px-6 py-4 text-slate-500">{periods?.find(p => p.period_id === pkg.period_id)?.name || "-"}</td>
                                            <td className="px-6 py-4 text-right sticky right-0 z-10 bg-white dark:bg-slate-900 group-hover:bg-slate-50/50 dark:group-hover:bg-slate-800/80 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)] transition-colors">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg relative"
                                                        onClick={() => setItineraryPackage(pkg)}
                                                        title="Kelola Rincian Jadwal"
                                                    >
                                                        <Clock className="w-4 h-4" />
                                                        {pkg.itineraries && pkg.itineraries.length > 0 && (
                                                            <span className="absolute -top-1.5 -right-1.5 bg-amber-600 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                                                                {pkg.itineraries.length}
                                                            </span>
                                                        )}
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-8 w-8 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg"
                                                        onClick={() => handleOpenDialog(pkg)}
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg"
                                                        onClick={() => handleDelete(pkg.package_id)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan={8} className="p-12 text-center text-slate-500">
                                                Belum ada data paket
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                    {/* Pagination Controls */}
                    {totalItems > 0 && (
                        <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                            <div className="flex items-center gap-3 text-slate-500 font-medium">
                                <span>Menampilkan {fromItem} - {toItem} dari {totalItems} data</span>
                                <div className="h-4 w-px bg-slate-300 dark:bg-slate-700 hidden sm:block"></div>
                                <div className="flex items-center gap-2 sm:flex">
                                    <label htmlFor="perPage">Tampilkan:</label>
                                    <select
                                        id="perPage"
                                        value={perPage}
                                        onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1 outline-none focus:ring-2 focus:ring-indigo-500 font-bold"
                                    >
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                </div>
                            </div>
                            
                            {totalPages > 1 && (
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        className="rounded-lg"
                                    >
                                        Sebelumnya
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                        className="rounded-lg"
                                    >
                                        Selanjutnya
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
            </div>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingPackage ? "Edit Paket" : "Tambah Paket Baru"}</DialogTitle>
                    </DialogHeader>
                    
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 pt-4">
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="period_id"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Periode <span className="text-red-500">*</span></FormLabel>
                                            <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Pilih Periode" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {periods?.map((p) => (
                                                        <SelectItem key={p.period_id} value={p.period_id.toString()}>
                                                            {p.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="parent_id"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Paket Induk (Kategori)</FormLabel>
                                            <Select onValueChange={field.onChange} defaultValue={field.value || "0"} value={field.value || "0"}>
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Tanpa Induk" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="0">-- Tanpa Induk (Berdiri Sendiri) --</SelectItem>
                                                    {parentOptions.map((p) => (
                                                        <SelectItem key={p.package_id} value={p.package_id.toString()}>
                                                            {p.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="code"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Kode Paket <span className="text-red-500">*</span></FormLabel>
                                            <FormControl>
                                                <Input placeholder="Contoh: PK-01" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Nama Paket <span className="text-red-500">*</span></FormLabel>
                                            <FormControl>
                                                <Input placeholder="Contoh: Peserta Reguler" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="price"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Harga (Rp) <span className="text-red-500">*</span></FormLabel>
                                        <FormControl>
                                            <Input type="number" min="0" placeholder="0" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Deskripsi</FormLabel>
                                        <FormControl>
                                            <Textarea placeholder="Deskripsi atau fasilitas yang didapatkan..." {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border p-4 rounded-xl shadow-sm">
                                <FormField
                                    control={form.control}
                                    name="is_header"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-row items-center justify-between">
                                            <div className="space-y-0.5">
                                                <FormLabel className="text-sm font-semibold">
                                                    Jadikan Sebagai Header?
                                                </FormLabel>
                                                <div className="text-xs text-muted-foreground">
                                                    Header tidak bisa dipilih peserta, hanya berfungsi sebagai grup / kategori.
                                                </div>
                                            </div>
                                            <FormControl>
                                                <Switch checked={field.value} onCheckedChange={field.onChange} />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                
                                <FormField
                                    control={form.control}
                                    name="is_quota"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-row items-center justify-between">
                                            <div className="space-y-0.5">
                                                <FormLabel className="text-sm font-semibold">
                                                    Batasi Kuota?
                                                </FormLabel>
                                                <div className="text-xs text-muted-foreground">
                                                    Aktifkan jika paket ini memiliki batas jumlah pendaftar.
                                                </div>
                                            </div>
                                            <FormControl>
                                                <Switch checked={field.value} onCheckedChange={field.onChange} />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            {isQuota && (
                                <FormField
                                    control={form.control}
                                    name="quota"
                                    render={({ field }) => (
                                        <FormItem className="animate-in fade-in zoom-in duration-300">
                                            <FormLabel>Jumlah Kuota</FormLabel>
                                            <FormControl>
                                                <Input type="number" min="0" placeholder="100" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            )}

                            <FormItem>
                                <FormLabel>Foto / Banner Paket</FormLabel>
                                <FormControl>
                                    <div className="flex items-center gap-4">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full flex items-center gap-2 border-dashed border-2 py-8 text-gray-500 hover:text-primary hover:border-primary"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <ImageIcon className="w-6 h-6" />
                                            {photoFile ? photoFile.name : "Klik untuk unggah foto (Max 2MB)"}
                                        </Button>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            className="hidden"
                                            accept="image/*"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) setPhotoFile(file);
                                            }}
                                        />
                                    </div>
                                </FormControl>
                                {editingPackage?.photo_path && !photoFile && (
                                    <div className="mt-2 text-sm text-muted-foreground flex items-center gap-2">
                                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-md text-xs">Ada Foto tersimpan</span>
                                    </div>
                                )}
                            </FormItem>

                            <div className="flex gap-3 pt-4 justify-end">
                                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-xl h-11 px-6 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                                    Batal
                                </Button>
                                <Button type="submit" disabled={isMutating} className="rounded-xl h-11 px-6 bg-indigo-600 hover:bg-indigo-700 text-white font-bold shadow-md">
                                    {isMutating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Simpan
                                </Button>
                            </div>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>

            {/* Custom Alert Dialog for Confirmations */}
            <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
                <AlertDialogContent className="rounded-3xl border-slate-200 dark:border-slate-800 p-6 shadow-2xl max-w-sm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                            {alertConfig?.title}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
                            {alertConfig?.description}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6 gap-2">
                        <AlertDialogCancel className="rounded-xl h-11 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium">Batal</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={() => {
                                alertConfig?.action();
                                setIsAlertOpen(false);
                            }}
                            className={`rounded-xl h-11 font-bold text-white shadow-md ${
                                alertConfig?.isDestructive 
                                    ? "bg-rose-600 hover:bg-rose-700" 
                                    : "bg-indigo-600 hover:bg-indigo-700"
                            }`}
                        >
                            {alertConfig?.actionText}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Dialog Kelola Rincian Jadwal (jam & aktivitas) */}
            <PackageItineraryDialog
                pkg={currentItineraryPackage}
                onClose={() => setItineraryPackage(null)}
            />
                </TabsContent>

                <TabsContent value="conference" className="mt-0 outline-none focus:outline-none">
                    <ConferencePackagesTab />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function PackageItineraryDialog({
    pkg, onClose,
}: {
    pkg: Package | null;
    onClose: () => void;
}) {
    const createMutation = useCreatePackageItinerary();
    const updateMutation = useUpdatePackageItinerary();
    const deleteMutation = useDeletePackageItinerary();

    const emptyForm = { start_time: "", end_time: "", activity: "", description: "" };
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState<number | null>(null);

    const resetForm = () => {
        setForm(emptyForm);
        setEditingId(null);
    };

    const handleEdit = (item: PackageItinerary) => {
        setEditingId(item.id);
        setForm({
            start_time: item.start_time || "",
            end_time: item.end_time || "",
            activity: item.activity,
            description: item.description || "",
        });
    };

    const handleSubmit = () => {
        if (!pkg || !form.activity.trim()) return;
        const payload = {
            start_time: form.start_time || null,
            end_time: form.end_time || null,
            activity: form.activity.trim(),
            description: form.description || null,
        };

        if (editingId) {
            updateMutation.mutate(
                { packageId: pkg.package_id, id: editingId, payload },
                {
                    onSuccess: () => { toast.success("Rincian jadwal diperbarui"); resetForm(); },
                    onError: (err: any) => toast.error(err.response?.data?.message || "Gagal memperbarui rincian jadwal"),
                }
            );
        } else {
            createMutation.mutate(
                { packageId: pkg.package_id, payload },
                {
                    onSuccess: () => { toast.success("Rincian jadwal ditambahkan"); resetForm(); },
                    onError: (err: any) => toast.error(err.response?.data?.message || "Gagal menambahkan rincian jadwal"),
                }
            );
        }
    };

    const handleDelete = (id: number) => {
        if (!pkg) return;
        if (!confirm("Hapus rincian jadwal ini?")) return;
        deleteMutation.mutate(
            { packageId: pkg.package_id, id },
            {
                onSuccess: () => toast.success("Rincian jadwal dihapus"),
                onError: (err: any) => toast.error(err.response?.data?.message || "Gagal menghapus rincian jadwal"),
            }
        );
    };

    const items = (pkg?.itineraries || []).slice().sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
    const isSaving = createMutation.isPending || updateMutation.isPending;

    return (
        <Dialog open={!!pkg} onOpenChange={(open) => { if (!open) { resetForm(); onClose(); } }}>
            <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Rincian Jadwal — {pkg?.name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-xs font-bold text-slate-500 block mb-1">Jam Mulai</label>
                            <Input type="time" value={form.start_time} onChange={(e) => setForm((v) => ({ ...v, start_time: e.target.value }))} />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-500 block mb-1">Jam Selesai</label>
                            <Input type="time" value={form.end_time} onChange={(e) => setForm((v) => ({ ...v, end_time: e.target.value }))} />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-slate-500 block mb-1">Kegiatan <span className="text-red-500">*</span></label>
                        <Input placeholder="Contoh: Registrasi Ulang" value={form.activity} onChange={(e) => setForm((v) => ({ ...v, activity: e.target.value }))} />
                    </div>
                    <div>
                        <label className="text-xs font-bold text-slate-500 block mb-1">Keterangan (Opsional)</label>
                        <Textarea
                            className="resize-none"
                            placeholder="Detail tambahan, lokasi, dsb."
                            value={form.description}
                            onChange={(e) => setForm((v) => ({ ...v, description: e.target.value }))}
                        />
                    </div>

                    <div className="flex justify-end gap-2">
                        {editingId && <Button variant="outline" onClick={resetForm}>Batal Edit</Button>}
                        <Button onClick={handleSubmit} disabled={isSaving || !form.activity.trim()} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                            {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {editingId ? "Simpan Perubahan" : "Tambah Rincian"}
                        </Button>
                    </div>

                    <div className="border-t border-slate-100 dark:border-slate-800 pt-4 space-y-2 max-h-[280px] overflow-y-auto">
                        {items.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Belum ada rincian jadwal.</p>}
                        {items.map((item) => (
                            <div key={item.id} className="flex items-start justify-between gap-2 p-2.5 rounded-xl border border-slate-100 dark:border-slate-800">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-1.5 text-xs font-bold text-indigo-600 dark:text-indigo-400">
                                        <Clock className="w-3 h-3" />
                                        {item.start_time || "-"}{item.end_time ? ` - ${item.end_time}` : ""}
                                    </div>
                                    <p className="font-semibold text-sm text-slate-800 dark:text-slate-100 truncate">{item.activity}</p>
                                    {item.description && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{item.description}</p>}
                                </div>
                                <div className="flex gap-1 shrink-0">
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(item)}><Edit2 className="w-3.5 h-3.5" /></Button>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => handleDelete(item.id)}><X className="w-3.5 h-3.5" /></Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
